import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { HomePageActivityModel } from '@/models/homePage/activity';

export interface HomePageState {
    nav: string;
    memberList: ListWrap<HomePage.MemberListItem> & ListRequesting;
    monitorList: ListWrap<HomePage.MonitorListItem> & ListRequesting;
    teamList: ListWrap<HomePage.TeamListItem> & ListRequesting;
    activityList: {
        [mode: string]: HomePageActivityModel[];
    };
    data: {
        insist: number;
        user_count: number;
        monitor: HomePage.MonitorListItem[];
        member: HomePage.MemberListItem[];
        team: HomePage.TeamListItem[];
    } | null;
    going: HomePage.Going[];
    hot: HomePage.Going[];
    knowledge: HomePage.Knowledge[];
}

const m: Module<HomePageState, RootState> = {
    namespaced: true,
    state: {
        nav: '',
        data: null,
        activityList: {},
        memberList: {
            list: [],
            loaded: false,
            requesting: false,
        },
        monitorList: {
            list: [],
            loaded: false,
            requesting: false,
        },
        teamList: {
            list: [],
            loaded: false,
            requesting: false,
        },
        going: [],
        hot: [],
        knowledge: [],
    },
    mutations: {
        setNav(state, nav) {
            state.nav = nav;
        },
        setData(state, data) {
            state.data = data;
        },
        /**
         * 活动列表
         */
        setActivityList(state, { mode, list }: { mode: Activity.Mode; list: HomePage.Activity[] }) {
            Vue.set(state.activityList, mode, list.map((item) => new HomePageActivityModel(item)));
        },
        /**
         * 成员列表
         */
        concatMemberList(state, { key, list }) {
            state.memberList.list = state.memberList.list.concat(list);
        },
        setMemberListLoaded(state, { key, loaded }) {
            state.memberList.loaded = loaded;
        },
        setMemberListReq(state, { key, req }) {
            state.memberList.requesting = req;
        },
        /**
         * 队长列表
         */
        concatMonitorList(state, { key, list }) {
            state.monitorList.list = state.monitorList.list.concat(list);
        },
        setMonitorListLoaded(state, { key, loaded }) {
            state.monitorList.loaded = loaded;
        },
        setMonitorListReq(state, { key, req }) {
            state.monitorList.requesting = req;
        },
        /**
         * 队伍列表
         */
        concatTeamList(state, { key, list }) {
            state.teamList.list = state.teamList.list.concat(list);
        },
        setTeamListLoaded(state, { key, loaded }) {
            state.teamList.loaded = loaded;
        },
        setTeamListReq(state, { key, req }) {
            state.teamList.requesting = req;
        },
        setGoing(state, list) {
            state.going = list;
        },
        setHot(state, list) {
            state.hot = list;
        },
        setKnowledge(state, { offset, forceUpdate, list, count }: { offset: number; count: number; forceUpdate: boolean; list: HomePage.Knowledge[] }) {
            list.forEach((item, idx) => {
                Vue.set(state.knowledge, idx + offset, item);
            });
            if (forceUpdate) {
                state.knowledge = state.knowledge.slice(0, count);
            }
        },
    },
    actions: {
        /**
         * 获取数据
         */
        async getData({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/home_page/data');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setData', rdata.data);
            }
            return rdata;
        },
        /**
         * 活动列表
         */
        async getActivityList({ state, commit }, { mode }) {
            const params = {}
            if(mode && mode !== 'all') {
                Object.assign(params, { mode })
            }
            const response = await Vue.axios.get('/api/v1/home_page/activity_list', {
                params
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setActivityList', {
                    mode,
                    list: rdata.data,
                });
            }
            return rdata;
        },
        async getActivityList2({ state, commit }, { mode }) {
            const params = {}
            Object.assign(params, { mode })
            const response = await Vue.axios.get('/api/v1/offline_activity', {
                params
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setActivityList', {
                    mode,
                    list: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 成员列表
         */
        async getMemberList({ state, commit }, { offset, count }) {
            const wrap = state.memberList;
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setMemberListReq', { req: true });
            const response = await Vue.axios.get('/api/v1/home_page/member_list', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatMemberList', { list });
                if (list.length < count) {
                    commit('setMemberListLoaded', { loaded: true });
                }
            }
            commit('setMemberListReq', { req: false });
            return rdata;
        },
        /**
         * 队长列表
         */
        async getMonitorList({ state, commit }, { offset, count }) {
            const wrap = state.monitorList;
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setMonitorListReq', { req: true });
            const response = await Vue.axios.get('/api/v1/home_page/monitor_list', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatMonitorList', { list });
                if (list.length < count) {
                    commit('setMonitorListLoaded', { loaded: true });
                }
            }
            commit('setMonitorListReq', { req: false });
            return rdata;
        },
        /**
         * 队伍列表
         */
        async getTeamList({ state, commit }, { offset, count }) {
            const wrap = state.teamList;
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setTeamListReq', { req: true });
            const response = await Vue.axios.get('/api/v1/home_page/team_list', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatTeamList', { list });
                if (list.length < count) {
                    commit('setTeamListLoaded', { loaded: true });
                }
            }
            commit('setTeamListReq', { req: false });
            return rdata;
        },
        async getGoing({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/home_page/activity/going');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setGoing', rdata.data);
            }
            return rdata;
        },
        async getHot({ state, commit }) {
            const response = await Vue.axios.get('/api/v1/home_page/activity/hot');
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setHot', rdata.data);
            }
            return rdata;
        },
        async getKnowledge({ state, commit }, {
            count,
            offset,
            forceUpdate,
        }) {
            const response = await Vue.axios.get('/api/v1/home_page/knowledge', {
                params: {
                    offset, count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setKnowledge', {
                    list: rdata.data,
                    forceUpdate,
                    count, offset,
                });
            }
            return rdata;
        },
    },
};

export default m;
